import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import withAnimation from '../withAnimation/withAnimation';
import WebEntryPanelHeader from './WebEntryPanelHeader';
import WebEntryPanelContent from './WebEntryPanelContent';
import { getUrlParams } from '../../utils/shared/urlUtils';
import { VALIDATION } from '../../constants/shared/WebEntryConstants';
import './WebEntryPanel.scss';

const { VALIDATION_ERROR } = VALIDATION;

const WebEntryPanel = ({
    staffData,
    isPmPanelType,
    panelType,
    period,
    studentsByGradeData,
    formIdsData,
    invalidateResult,
    prepareAndCreateResult,
    rowStatuses,
    location,
    fetchStaff,
    fetchResults,
    fetchFormIds,
    clearWebEntryData,
    onCloseWrappedComponent,
    isChosenSchoolWindowDatesStrictlyEnforced,
    webEntryPanelConfig,
    isSecurePeriod,
}) => {
    const {
        assessmentType,
        webEntryClassName,
        getErrors,
        panelClassName,
        headerSection,
        tableData,
    } = webEntryPanelConfig;

    const [errors, setErrors] = useState({});

    const processValidationAndSaving = (
        rowData,
        studentId,
        temporalStudentId,
        grade,
        setIsRowFocused,
        temporalInstId,
    ) => {
        const { fields, errorObject } = getErrors(rowData);
        if (errorObject.error) {
            setErrors(prevState => ({ ...prevState, [studentId]: errorObject }));
        } else {
            setErrors(prevState => {
                const tempObject = { ...prevState };
                delete tempObject[studentId];
                return tempObject;
            });
            setIsRowFocused(false);
            prepareAndCreateResult(
                fields,
                grade,
                rowData.formValues.formId,
                studentId,
                period,
                temporalStudentId,
                temporalInstId,
            );
        }
    };

    const handleOnDone = () => {
        fetchResults();
        onCloseWrappedComponent();
    };

    useEffect(() => {
        if (isEmpty(staffData) || staffData.some(item => !item)) {
            fetchStaff();
        }
        if (isEmpty(formIdsData)) {
            fetchFormIds(period);
        }
        return () => {
            clearWebEntryData();
        };
    }, []);

    return (
        <>
            <WebEntryPanelHeader
                {...headerSection}
                webEntryClassName={webEntryClassName}
                onClose={handleOnDone}
                hasErrors={!isEmpty(errors)}
            />

            <div className={`${webEntryClassName}-web-entry ${panelClassName}`}>
                <div className={`content ${!isEmpty(errors) ? VALIDATION_ERROR : ''}`}>
                    <WebEntryPanelContent
                        headerCells={tableData.headerCells}
                        rowsData={tableData.rows}
                        studentsByGradeData={studentsByGradeData}
                        webEntryClassName={webEntryClassName}
                        isPmPanelType={isPmPanelType}
                        panelType={panelType}
                        rowStatuses={rowStatuses}
                        invalidateResult={invalidateResult}
                        isEditable={Boolean(getUrlParams(location).isEditable)}
                        errors={errors}
                        processValidationAndSaving={processValidationAndSaving}
                        assessmentType={assessmentType}
                        isChosenSchoolWindowDatesStrictlyEnforced={
                            isChosenSchoolWindowDatesStrictlyEnforced
                        }
                        isSecurePeriod={isSecurePeriod}
                    />
                </div>
            </div>
        </>
    );
};

WebEntryPanel.defaultProps = {
    isPmPanelType: false,
    panelType: null,
    period: '',
    formIdsData: [],
    rowStatuses: null,
    studentsByGradeData: [],
    staffData: [],
    isChosenSchoolWindowDatesStrictlyEnforced: false,
    fetchStaff: () => {},
    fetchFormIds: () => {},
    onCloseWrappedComponent: () => {},
    fetchResults: () => {},
    clearWebEntryData: () => {},
    invalidateResult: () => {},
    prepareAndCreateResult: () => {},
    isSecurePeriod: false,
};

WebEntryPanel.propTypes = {
    isPmPanelType: PropTypes.bool,
    panelType: PropTypes.string,
    studentsByGradeData: PropTypes.array,
    staffData: PropTypes.array,
    fetchStaff: PropTypes.func,
    fetchFormIds: PropTypes.func,
    formIdsData: PropTypes.array,
    isChosenSchoolWindowDatesStrictlyEnforced: PropTypes.bool,
    onCloseWrappedComponent: PropTypes.func,
    fetchResults: PropTypes.func,
    clearWebEntryData: PropTypes.func,
    period: PropTypes.string,
    invalidateResult: PropTypes.func,
    prepareAndCreateResult: PropTypes.func,
    rowStatuses: PropTypes.object,
    location: PropTypes.object.isRequired,
    webEntryPanelConfig: PropTypes.object.isRequired,
    isSecurePeriod: PropTypes.bool,
};

export default withAnimation(WebEntryPanel);
